// Shared building blocks for the V1 (dense desk) and V2 (context-aware) home screens, 2026-10-04.
// Plain presentational pieces: they take data as props and make no requests (the data comes from lib/useDeskData.js).
import React from 'react'
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import { timeAgo } from '../lib/utils'
import { dailyBarsStale, healthIssues } from '../lib/health'

export const MONO = { fontFamily: 'JetBrains Mono, monospace' }
export const num = (v, d = 2) => (v == null || Number.isNaN(Number(v)) ? '—' : Number(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }))
export const signed = (v, d = 2) => (v == null || Number.isNaN(Number(v)) ? '—' : `${Number(v) >= 0 ? '+' : ''}${num(v, d)}`)
const GREEN = '#34d399'
const RED = '#fb7185'

export function Panel({ title, right, children, style, bodyStyle, grow, className = '' }) {
  return (
    <div className={`rounded border border-gray-800 flex flex-col min-h-0 min-w-0 ${className}`} style={{ background: '#111827', ...(grow ? { flex: 1 } : {}), ...style }}>
      {title && (
        <div className="flex items-center gap-2 px-2.5 py-1 flex-shrink-0" style={{ ...MONO, fontSize: '0.62rem', letterSpacing: '0.08em', color: '#64748b', borderBottom: '1px solid #1e293b' }}>
          <span className="uppercase font-semibold">{title}</span>
          <span className="flex-1" />
          {right}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto" style={{ padding: '6px 8px', ...bodyStyle }}>{children}</div>
    </div>
  )
}

export function KV({ rows }) {
  return (
    <div style={{ ...MONO, fontSize: '0.7rem', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 10px' }}>
      {rows.map(([k, v, tone]) => (
        <React.Fragment key={k}>
          <span style={{ color: '#64748b' }}>{k}</span>
          <span style={{ textAlign: 'right', color: tone === 'bad' ? '#fca5a5' : tone === 'warn' ? '#fcd34d' : tone === 'good' ? GREEN : '#cbd5e1' }}>{v}</span>
        </React.Fragment>
      ))}
    </div>
  )
}

// rows: [{symbol, qty, last, pnl_pct, has_stop (true|false|null = not applicable), held_days, book?}]
export function PositionsTable({ rows, selected, onSelect, showBook }) {
  return (
    <table className="w-full" style={{ ...MONO, fontSize: '0.68rem', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ color: '#475569' }}>
          {[showBook ? 'Bk' : null, 'Sym', 'Qty', 'Last', 'P&L%', 'Stop', 'Age d'].filter(Boolean).map((h, i) => (
            <th key={h} className={h === 'Qty' ? 'c-qty' : h === 'Age d' ? 'c-age' : undefined} style={{ textAlign: h === 'Sym' || h === 'Bk' ? 'left' : 'right', padding: '2px 5px', fontWeight: 500, position: 'sticky', top: 0, background: '#111827', fontSize: '0.58rem' }}>{h.toUpperCase()}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(p => (
          <tr key={`${p.book || ''}${p.symbol}`} onClick={() => onSelect && onSelect(p.symbol)}
            style={{ cursor: onSelect ? 'pointer' : 'default', height: 20, borderTop: '1px solid rgba(30,41,59,0.6)',
                     background: p.has_stop === false ? 'rgba(127,29,29,0.35)' : selected === p.symbol ? 'rgba(23,48,71,0.9)' : 'transparent' }}>
            {showBook && <td style={{ padding: '1px 5px', color: '#64748b' }}>{p.book}</td>}
            <td style={{ padding: '1px 5px', color: '#e2e8f0', fontWeight: 600 }}>{p.symbol}</td>
            <td className="c-qty" style={{ padding: '1px 5px', textAlign: 'right' }}>{num(p.qty, 0)}</td>
            <td style={{ padding: '1px 5px', textAlign: 'right' }}>{num(p.last)}</td>
            <td style={{ padding: '1px 5px', textAlign: 'right', color: (p.pnl_pct ?? 0) >= 0 ? GREEN : RED }}>{signed(p.pnl_pct)}</td>
            <td style={{ padding: '1px 5px', textAlign: 'right', fontWeight: 700, color: p.has_stop === false ? '#fca5a5' : p.has_stop ? GREEN : '#475569' }}>
              {p.has_stop === false ? 'NO STOP' : p.has_stop ? '✓' : '—'}
            </td>
            <td className="c-age" style={{ padding: '1px 5px', textAlign: 'right', color: '#94a3b8' }}>{p.held_days == null ? '—' : Number(p.held_days).toFixed(1)}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={7} style={{ padding: 12, textAlign: 'center', color: '#64748b' }}>no positions</td></tr>}
      </tbody>
    </table>
  )
}

export const mainRows = (main) => (main?.positions || []).map(p => ({
  book: 'MAIN', symbol: p.symbol, qty: p.qty, last: p.current_price, pnl_pct: p.pnl_pct, has_stop: p.has_stop, held_days: p.held_days,
  entry: p.avg_entry_price, value: p.market_value, pnl: p.unrealized_pl, stop_price: p.stop_price,
}))
export const tier2Rows = (t2) => (t2?.positions || []).map(p => ({
  book: 'T2', symbol: p.symbol, qty: p.qty, last: p.current_price, pnl_pct: p.pnl_pct, has_stop: null,
  held_days: p.minutes_held == null ? null : p.minutes_held / 1440, entry: p.entry_price, value: null, pnl: p.pnl_dollar, stop_price: null,
}))

export function RiskPanel({ account, main, tier2 }) {
  const s = main?.summary
  const pv = account?.portfolio_value
  const exposure = s && pv ? (s.market_value / pv) * 100 : null
  return (
    <KV rows={[
      ['portfolio', pv == null ? '—' : `$${num(pv, 0)}`],
      ['buying power', account?.buying_power == null ? '—' : `$${num(account.buying_power, 0)}`],
      ['MAIN exposure', exposure == null ? '—' : `${exposure.toFixed(0)}% · $${num(s.market_value, 0)}`],
      ['unrealized (MAIN)', s ? signed(s.unrealized_pl) : '—'],
      ['unprotected', s ? (s.unprotected_count > 0 ? `${s.unprotected_count}: ${s.unprotected.slice(0, 4).join(', ')}` : 'none') : '—', s && s.unprotected_count > 0 ? 'bad' : 'good'],
      ['day trades', account ? (account.pattern_day_trader ? `${account.daytrade_count}` : `${account.daytrade_count}/3`) : '—'],
      ['T2 positions', tier2 ? tier2.count ?? (tier2.positions || []).length : '—'],
      ['breakers', 'not exposed by the API yet'],
    ]} />
  )
}

const AGE = (s) => (s == null ? '—' : s < 90 ? `${Math.round(s)}s` : s < 5400 ? `${Math.round(s / 60)}m` : `${(s / 3600).toFixed(1)}h`)

export function EnginePanel({ health, ws, workers = true }) {
  const w = health?.workers || {}
  const bad = Object.entries(w).filter(([, v]) => v && v.last_ok === false)
  const issues = healthIssues({ health, ws })
  const has = (k) => issues.some(i => i.key === k)
  return (
    <div>
      <KV rows={[
        ['status', health?.status ?? '—', health?.status === 'healthy' ? 'good' : 'bad'],
        ['scheduler / watchdog', `${health?.scheduler_alive ? 'alive' : 'DEAD'} / ${health?.watchdog_alive ? 'alive' : 'DEAD'}`, health && health.scheduler_alive && health.watchdog_alive ? 'good' : 'bad'],
        ['stream', ws ? `${ws.status}${ws.seconds_since_tick != null ? ` · tick ${AGE(ws.seconds_since_tick)}` : ''}` : '—', has('stream') ? (ws?.status === 'FAILED' ? 'bad' : 'warn') : undefined],
        ['latest bar', health?.latest_data ? timeAgo(health.latest_data) : '—', has('data') ? 'bad' : undefined],
        ['source', health?.last_collection_source ?? '—'],
        ['daily bars', health?.daily_bars_latest ? `${health.daily_bars_latest}${dailyBarsStale(health) ? ' · behind' : ''}` : '—', dailyBarsStale(health) ? 'warn' : undefined],
        ['session', health?.market_session ?? '—'],
      ]} />
      {workers && (
        <table className="w-full mt-2" style={{ ...MONO, fontSize: '0.62rem', borderCollapse: 'collapse' }}>
          <tbody>
            {Object.entries(w).filter(([k]) => !/^(asyncio|ThreadPool)/.test(k)).map(([k, v]) => (
              <tr key={k} style={{ borderTop: '1px solid rgba(30,41,59,0.6)' }}>
                <td style={{ color: '#94a3b8', padding: '1px 4px' }}>{k}</td>
                <td style={{ textAlign: 'right', color: v.last_ok === false ? '#fca5a5' : v.last_ok ? GREEN : '#64748b', padding: '1px 4px' }}>{v.last_ok === false ? 'FAILED' : v.last_ok ? 'ok' : (v.running ? 'running' : '—')}</td>
                <td style={{ textAlign: 'right', color: '#64748b', padding: '1px 4px' }}>{v.last_finish_age_s == null ? '' : `${AGE(v.last_finish_age_s)} ago`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {bad.length > 0 && <p style={{ ...MONO, fontSize: '0.62rem', color: '#fca5a5', marginTop: 4 }}>{bad.length} worker(s) last run failed</p>}
    </div>
  )
}

export function EquityChart({ data, period, onPeriod, height = 130, fontSize = 8 }) {
  const rows = data || []
  const up = rows.length > 1 ? rows[rows.length - 1].equity >= rows[0].equity : true
  const col = up ? '#10b981' : '#f43f5e'
  return (
    <div style={{ height }}>
      {onPeriod && (
        <div className="flex gap-1 justify-end" style={{ marginBottom: 2 }}>
          {['1D', '1W', '1M'].map(p => (
            <button key={p} onClick={() => onPeriod(p)} style={{ ...MONO, fontSize: '0.58rem', padding: '1px 6px', borderRadius: 3, border: '1px solid #374151', background: period === p ? '#1d4ed8' : 'transparent', color: period === p ? '#bfdbfe' : '#64748b' }}>{p}</button>
          ))}
        </div>
      )}
      <ResponsiveContainer width="100%" height={onPeriod ? height - 18 : height}>
        <AreaChart data={rows} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
          <CartesianGrid strokeDasharray="2 5" stroke="#1e293b" />
          <XAxis dataKey="time" tickFormatter={ts => new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            tick={{ fontSize, fill: fontSize >= 12 ? '#8b9bb0' : '#475569', fontFamily: 'JetBrains Mono, monospace' }} stroke="#1e293b" interval="preserveStartEnd" />
          <YAxis domain={['auto', 'auto']} tick={{ fontSize, fill: fontSize >= 12 ? '#8b9bb0' : '#475569', fontFamily: 'JetBrains Mono, monospace' }} stroke="#1e293b" width={fontSize >= 12 ? 56 : 44} tickFormatter={v => `${Math.round(v / 100) / 10}k`} />
          <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', fontSize: fontSize >= 12 ? 12 : 11 }} labelFormatter={ts => new Date(ts * 1000).toLocaleString()} formatter={(v) => [`$${num(v, 0)}`, 'equity']} />
          <Area type="monotone" dataKey="equity" stroke={col} fill={col} fillOpacity={0.12} strokeWidth={1.5} dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

export function SignalsList({ signals }) {
  if (!signals || signals.length === 0) return <p style={{ ...MONO, fontSize: '0.68rem', color: '#64748b', padding: 8 }}>no signals in the window</p>
  return (
    <table className="w-full" style={{ ...MONO, fontSize: '0.68rem', borderCollapse: 'collapse' }}>
      <tbody>
        {signals.slice(0, 40).map((s, i) => (
          <tr key={i} style={{ borderTop: '1px solid rgba(30,41,59,0.6)', height: 20 }}>
            <td style={{ padding: '1px 5px', color: '#64748b' }}>{s.time || s.timestamp ? timeAgo(s.time || s.timestamp) : ''}</td>
            <td style={{ padding: '1px 5px', color: '#e2e8f0', fontWeight: 600 }}>{s.symbol}</td>
            <td style={{ padding: '1px 5px', color: String(s.direction).toLowerCase().includes('sell') ? RED : GREEN }}>{s.direction}</td>
            <td style={{ padding: '1px 5px', textAlign: 'right', color: '#94a3b8' }}>{s.strength != null ? num(s.strength, 2) : ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function OrdersList({ orders }) {
  if (!orders || orders.length === 0) return <p style={{ ...MONO, fontSize: '0.68rem', color: '#64748b', padding: 8 }}>no recent orders</p>
  return (
    <table className="w-full" style={{ ...MONO, fontSize: '0.68rem', borderCollapse: 'collapse' }}>
      <tbody>
        {orders.slice(0, 40).map(o => (
          <tr key={o.id} style={{ borderTop: '1px solid rgba(30,41,59,0.6)', height: 20 }}>
            <td style={{ padding: '1px 5px', color: '#64748b' }}>{o.submitted_at ? timeAgo(o.submitted_at) : ''}</td>
            <td style={{ padding: '1px 5px', color: '#e2e8f0', fontWeight: 600 }}>{o.symbol}</td>
            <td style={{ padding: '1px 5px', color: String(o.side).toLowerCase().includes('sell') ? RED : GREEN }}>{String(o.side).replace(/^OrderSide\./i, '')}</td>
            <td style={{ padding: '1px 5px', textAlign: 'right' }}>{num(o.filled_qty ?? 0, 0)}/{num(o.qty, 0)}</td>
            <td style={{ padding: '1px 5px', textAlign: 'right', color: '#94a3b8' }}>{o.avg_fill_price ? num(o.avg_fill_price) : ''}</td>
            <td style={{ padding: '1px 5px', textAlign: 'right', color: '#64748b' }}>{String(o.status).replace(/^OrderStatus\./i, '')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
